import { tt } from './i18n'
import { categoryColour, type CategoryColour } from './category-colour'

/**
 * §9.5's channel filter — *"kitchen + orders boards get channel filter tabs (each channel, multi-select,
 * with an all-channels tab)"*.
 *
 * It lives in `@agos/shared` because two modules need the same answer and §5 forbids one owning it for the
 * other: `/kitchen` and `/lists` are both `manager-orders`, but `/preorders`, `/dispatch` and the kasa
 * screens all label a channel too, and every one of them currently hard-codes the same three names.
 *
 * **This replaces `ORDER_TYPE_LABELS`**, whose own docblock said so:
 *
 * > The three channels legacy hardcodes. BACKEND.md §10A.1 replaced them with franchise-managed
 * > `sales_channels`, and §9.5 replaces this map with a channel lookup — after parity.
 *
 * And `SalesChannel`'s docblock says why it matters: *"Ask what a channel does, never what it is called.
 * `channel.usesDrivers()` keeps working when a franchise adds a fourth channel; `"delivery".equals(type)`
 * does not."* The same holds for the label — a hard-coded map cannot name a channel it has never heard of.
 *
 * **FB-174's other half.** The map labels `bar` *Osobný odber* while `sales_channels` names it **BAR**. Two
 * names for one channel, on screens a manager reads side by side. The server's name wins, because it is the
 * one the franchise chose and the one every §10A payload carries.
 */

export interface FilterChannel {
  slug: string
  name: string
  isActive: boolean
  sortOrder: number
  /**
   * The capabilities, for the screens that place an order rather than list one.
   *
   * `SalesChannel`: *"Ask what a channel **does**, never what it is called."* The order builder asked what
   * it was called — `orderType === 'restaurant'` for the table field, `=== 'delivery'` for the address —
   * so a fourth channel would have collected neither.
   */
  requiresDeliveryAddress: boolean
  usesTables: boolean
  usesDrivers: boolean
  onPremise: boolean
  allowsDeferredPayment: boolean
  /**
   * §10A.87 — paid and printed the moment it is taken, so it is never on a board.
   *
   * The owner's *Do ruky*. Every other capability here says what an order **needs**; this one says
   * when it is **finished**.
   */
  settlesImmediately: boolean
  /**
   * §10A.331, owner O44 - the till offers the product's own add-ons after adding it.
   *
   * The owner: *"can you make also setting in Predajné Kanály - to show these modals for čašníčka?"*. Off by
   * default, because a question that interrupts a waiter is a decision a franchise makes rather than one a
   * migration makes for them.
   */
  offersAddons: boolean
  /**
   * §10A.347, §9.688, owner #24 (O79) - the channel runs its own *Kuchyňa* and *Otvorené stoly*.
   *
   * The terrace's *OFYR* pair. Read only together with `isActive`, through `ownBoardChannels` in
   * `channel-boards.ts`: a switched-off channel has no boards of its own, so its orders are drawn on the
   * ordinary ones.
   */
  ownBoards: boolean
  /**
   * §10A.374, §9.726, owner O96 - on a channel that settles immediately, whether *Zatvoriť bez fiškálneho
   * dokladu* **cancels** the order (§9.457, Šurany's rule and the column's default) or closes it as paid.
   *
   * Read only through {@link closeWithoutDocumentCancels}, which asks it together with `settlesImmediately` -
   * on any other channel such a close has always settled.
   */
  unprintedCloseCancels: boolean
  /**
   * §10A.443, §9.792, owner O144 - whether *Rozvoz - nástenka* may offer this channel's orders to external couriers.
   *
   * Read only through {@link offersToCouriers}; on a channel no driver takes it is stored and means nothing.
   */
  offersToCouriers: boolean
  /**
   * §10A.388, §9.740, owner O110 - the registers the person printing may choose from, in the order they were
   * ticked. Empty means *no list*, and every screen then offers what it offered before.
   *
   * Read only through {@link printRegistersOf}, which honours it on a channel both on the premises and driven -
   * the only place the setting is offered, so a list left behind on a channel that stopped driving narrows
   * nothing.
   */
  printKasaIds: readonly string[]
  /**
   * §9.38 — the channel's colour, or `null` when it has none or one this build does not know.
   *
   * Read through `categoryColour`, the same gate a category's colour passes, because the owner asked
   * for *farbičkovanie ako pri kategóriách* and one rule means an unrecognised value renders as no
   * colour on both screens rather than as a broken chip on one.
   */
  colour: CategoryColour | null
}

/** The ids of a `text[]` as the server answers it - strings only, trimmed, the blank and the repeated dropped. */
function registerIdsOf(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const ids: string[] = []
  for (const entry of value) {
    const id = typeof entry === 'string' ? entry.trim() : ''
    if (id !== '' && !ids.includes(id)) ids.push(id)
  }
  return ids
}

export function filterChannelsOf(payload: unknown): FilterChannel[] {
  if (!Array.isArray(payload)) return []
  return payload
    .flatMap((entry) => {
      const row = entry as Record<string, unknown>
      const slug = typeof row.slug === 'string' && row.slug !== '' ? row.slug : null
      if (slug === null) return []
      return [
        {
          slug,
          name: typeof row.name === 'string' && row.name !== '' ? row.name : slug,
          isActive: row.isActive === true,
          sortOrder: typeof row.sortOrder === 'number' ? row.sortOrder : 0,
          requiresDeliveryAddress: row.requiresDeliveryAddress === true,
          usesTables: row.usesTables === true,
          usesDrivers: row.usesDrivers === true,
          onPremise: row.onPremise === true,
          allowsDeferredPayment: row.allowsDeferredPayment === true,
          settlesImmediately: row.settlesImmediately === true,
          offersAddons: row.offersAddons === true,
          // §10A.347 - `=== true`, so a server older than V74 reads as *no own boards*, which leaves every
          // order on the ordinary Kuchyňa and Otvorené stoly - the direction in which nothing is hidden.
          ownBoards: row.ownBoards === true,
          // §10A.374 - **`!== false`, the other way round from every flag above.** The column defaults to
          // true (§9.457's cancellation), so a server older than V79, which does not send it, must read as
          // today's cancellation rather than as a settling close nobody chose.
          unprintedCloseCancels: row.unprintedCloseCancels !== false,
          // §10A.443 - `!== false` too: the column defaults to true, the couriers' button every channel had, so a
          // server older than V102 reads as that rather than as an off nobody chose.
          offersToCouriers: row.offersToCouriers !== false,
          // §10A.388 - a server older than V84 sends nothing, which is *no list*: every screen as before.
          printKasaIds: registerIdsOf(row.printKasaIds),
          // The wire spells it `color`; the helpers it is used with are `categoryColour`.
          colour: categoryColour(row.color),
        },
      ]
    })
    .sort((left, right) => left.sortOrder - right.sortOrder || left.slug.localeCompare(right.slug))
}

/**
 * The channels to offer as filter tabs.
 *
 * Active ones, **plus any inactive channel the visible orders actually use** — switching a channel off
 * stops new orders choosing it, it does not remove the ones already placed, and a board that hid the tab
 * would leave those orders unfilterable and unexplained.
 */
export function offeredChannels(
  channels: readonly FilterChannel[],
  usedSlugs: readonly string[],
): FilterChannel[] {
  const used = new Set(usedSlugs)
  return channels.filter((channel) => channel.isActive || used.has(channel.slug))
}

/**
 * What to call a channel, from the server when it knows and from the slug when it does not.
 *
 * A slug with no channel behind it is shown as itself rather than blank: an order carrying a type the
 * channel table has never heard of is exactly the case worth seeing.
 */
export function channelName(slug: string, channels: readonly FilterChannel[]): string {
  const match = channels.find((channel) => channel.slug === slug)
  return match?.name ?? (slug === '' ? tt('bez kanála') : slug)
}

// `isKnownChannel` and `unknownSlugs` were here and are deleted — §9.35, 2026-08-06. They named the
// order types no channel explains, and no screen asked. `unknownSlugs` had no caller and
// `isKnownChannel` had exactly one, inside it, which is the chain that showed the sweep's own script
// was still missing wrapped signatures.
//
// Measured before deleting: every order type in the data — `delivery` 7 436, `restaurant` 2 266,
// `bar` 1 086 — has a channel row, so the list they built was empty.
//
// **The case they guarded is already handled where it shows.** `channelName` above answers an
// unexplained slug with the slug itself rather than a blank, so an order carrying a type the table
// has never heard of appears in the filter reading `vydaj` — visible, and in the one place somebody
// is looking. A second, silent list of the same slugs adds nothing to that.

// ─── The selection ───────────────────────────────────────────────────────────

/**
 * A multi-select where **empty means all**.
 *
 * Not a set containing every slug: a franchise that adds a channel while somebody has the board open
 * should have it included, and *everything* is a different intent from *these four, which happen to be all
 * of them today*.
 */
export type ChannelSelection = readonly string[]

export const ALL_CHANNELS: ChannelSelection = []

export function isAllSelected(selection: ChannelSelection): boolean {
  return selection.length === 0
}

export function isSelected(slug: string, selection: ChannelSelection): boolean {
  return isAllSelected(selection) || selection.includes(slug)
}

/**
 * Toggling one channel.
 *
 * From *all*, tapping one narrows to that one — which is what somebody wants when they tap a tab on a busy
 * board. Deselecting the last one goes back to *all* rather than to a board showing nothing, because an
 * empty board is indistinguishable from a quiet evening.
 */
export function toggleChannel(slug: string, selection: ChannelSelection): ChannelSelection {
  if (isAllSelected(selection)) return [slug]
  if (selection.includes(slug)) {
    const next = selection.filter((entry) => entry !== slug)
    return next.length === 0 ? ALL_CHANNELS : next
  }
  return [...selection, slug]
}

export function selectAll(): ChannelSelection {
  return ALL_CHANNELS
}

/** Whether an order belongs on the board given the current selection. */
export function matchesSelection(slug: string, selection: ChannelSelection): boolean {
  return isAllSelected(selection) || selection.includes(slug)
}

export function filterByChannel<T extends { type?: string | null }>(
  rows: readonly T[],
  selection: ChannelSelection,
): T[] {
  if (isAllSelected(selection)) return [...rows]
  return rows.filter((row) => matchesSelection(row.type ?? '', selection))
}

/**
 * What the board is showing, said so a narrowed board cannot be mistaken for a quiet one.
 *
 * The failure this prevents is specific: somebody taps *Rozvoz*, walks away, comes back to an empty board
 * and reads it as *no orders* when it means *no delivery orders*.
 */
export function selectionNote(
  selection: ChannelSelection,
  channels: readonly FilterChannel[],
  shown: number,
  total: number,
): string {
  if (isAllSelected(selection)) return ''
  const names = selection.map((slug) => channelName(slug, channels)).join(', ')
  if (shown === 0) {
    return `${tt('Filtrujete na')} ${names} - ${tt('a v tomto kanáli nič nie je. Celkovo je tu')} ${total}.`
  }
  return `${tt('Zobrazené len')} ${names} (${shown} ${tt('z')} ${total}).`
}

/** The label on the *all* tab, which is a tab like any other so a thumb can reach it. */
export function allLabel(): string {
  return tt('Všetky')
}

/**
 * A selection that survives a poll but not a session.
 *
 * The boards refetch every ten seconds and re-render; the filter must not reset under somebody's hand.
 * It is deliberately **not** persisted across sessions — a kitchen screen left filtered overnight is how
 * an order gets missed, and the next shift must open on everything.
 */
export function sanitiseSelection(
  selection: ChannelSelection,
  offered: readonly FilterChannel[],
): ChannelSelection {
  const known = new Set(offered.map((channel) => channel.slug))
  const kept = selection.filter((slug) => known.has(slug))
  return kept.length === selection.length ? selection : kept
}

// ─── What a channel needs before an order can be placed on it ────────────────

/**
 * The fields a new order on this channel must carry.
 *
 * Read off the **capabilities**, never the slug. The order builder asked
 * `orderType === 'restaurant'` for the table number and `=== 'delivery'` for the address and phone, so a
 * franchise adding a fourth channel would have been asked for neither — and `SalesChannel`'s own docblock
 * is explicit that this is the mistake to avoid.
 *
 * The phone follows the address: a delivery needs somebody to ring from the door, and every channel that
 * requires an address in this data also uses drivers.
 */
export interface ChannelRequirements {
  address: boolean
  phone: boolean
  table: boolean
}

export function requirementsFor(
  slug: string,
  channels: readonly FilterChannel[],
): ChannelRequirements {
  const channel = channels.find((entry) => entry.slug === slug)
  if (channel === undefined) {
    // An unknown channel asks for nothing rather than guessing: the server validates, and inventing a
    // requirement would block an order the server would have accepted.
    return { address: false, phone: false, table: false }
  }
  return {
    address: channel.requiresDeliveryAddress,
    phone: channel.requiresDeliveryAddress,
    table: channel.usesTables,
  }
}

/**
 * Whether the till settles this channel on the spot — §10A.87, the owner's *Do ruky*.
 *
 * A separate question from `belongsOnRoad`, though the same flag answers both: one decides where an
 * order is looked after, this one decides what the person who took it does next.
 *
 * **An unknown channel does not settle itself.** Same reasoning as the board: a channel nobody can
 * resolve must leave its order somewhere it will be noticed.
 */
export function settlesAtOnce(slug: string, channels: readonly FilterChannel[]): boolean {
  return channels.find((entry) => entry.slug === slug)?.settlesImmediately === true
}

/**
 * Whether *Zatvoriť bez fiškálneho dokladu* **cancels** an order on this channel - §9.726, `BACKEND.md` §10A.374,
 * owner O96.
 *
 * **The client's one copy of `CloseWithoutDocumentService.cancelsRatherThanSettles`**, so the note under the
 * button, the question's wording, its button and its colour are all drawn from one answer. §9.457 made such a
 * close on a settling channel a cancellation (Šurany: a counter sale with no printout did not happen); Sereď
 * takes the money and sometimes prints nothing - *"chce zatvoriť bez fiškálneho dokladu nahadzuje mu storno"* -
 * so the channel's `unprintedCloseCancels` decides, on by default. **The server still decides what happens** and
 * says so in `outcome`; this is what the screen promises before the press.
 *
 * **An unknown channel does not cancel**, for the same reason the server's does not: a slug nobody can resolve
 * falls back to a counter channel that does not settle at once, whose close without a document settles.
 */
export function closeWithoutDocumentCancels(slug: string, channels: readonly FilterChannel[]): boolean {
  const channel = channels.find((entry) => entry.slug === slug)
  if (channel === undefined) return false
  return channel.settlesImmediately && channel.unprintedCloseCancels
}

/**
 * Whether the till offers a product's add-ons on this channel - §9.657, owner O44.
 *
 * **Two conditions, and the second is read off a capability that already exists rather than added.** A channel
 * that settles immediately opens its payment and prints its document on save (§9.457), so there is no moment
 * between the add and the close in which to ask - and a prompt arriving after the receipt printed would be
 * offering to charge for something the document does not carry.
 *
 * **An unknown channel offers nothing**, which is this file's own rule for every other capability: inventing an
 * interruption for a channel nobody can resolve is worse than not offering an upsell.
 */
export function offersAddonsOn(slug: string, channels: readonly FilterChannel[]): boolean {
  const channel = channels.find((entry) => entry.slug === slug)
  if (channel === undefined) return false
  return channel.offersAddons && !channel.settlesImmediately
}

/** The channels a *new* order may be placed on. A switched-off channel is not one of them. */
export function orderableChannels(channels: readonly FilterChannel[]): FilterChannel[] {
  return channels.filter((channel) => channel.isActive)
}

// ─── Where an order belongs, for the dispatch board ──────────────────────────

/**
 * Whether an order on this channel belongs on the dispatch board.
 *
 * The board is the road. A channel that serves tables belongs to the restaurant screen — which is what
 * `type !== 'restaurant'` meant, and what it stopped meaning the moment a franchise added a second
 * table-serving channel. `na_sekeru` is exactly that channel, and it would have appeared on the road.
 */
export function belongsOnRoad(slug: string, channels: readonly FilterChannel[]): boolean {
  const channel = channels.find((entry) => entry.slug === slug)
  // A channel the table has never heard of stays on the board: an order nobody can see is worse than an
  // order on the wrong screen, and the dispatcher can tell at a glance.
  if (channel === undefined) return true
  // §10A.87 — a channel that settles at once is finished before the board could draw it. Checked
  // before `usesTables`, because such a channel has no tables either and would otherwise pass.
  if (channel.settlesImmediately) return false
  return !channel.usesTables
}

/**
 * Whether anybody drives an order on this channel - §9.309, the owner's item B.
 *
 * **The channel's own capability, not an inference from the other flags.** `sales-channels.ts` states
 * the rule this file lives by: *"Ask what a channel **does**, never what it is called."* The first
 * draft of §9.309 asked `!isCounterCollection` instead and would have been wrong on the owner's data:
 * `bar` has `usesDrivers: true` and **137 of its 1 082 orders have a driver** (FB-181 measured that),
 * so a counter channel here really is driven one time in eight.
 *
 * **An unresolvable channel counts as driven**, the same way `belongsOnRoad` keeps it on the board: a
 * dispatcher who cannot hand over an order they can see is worse off than one offered a picker that
 * turns out to be unnecessary.
 */
export function usesDrivers(slug: string, channels: readonly FilterChannel[]): boolean {
  const channel = channels.find((entry) => entry.slug === slug)
  if (channel === undefined) return true
  return channel.usesDrivers
}

/**
 * Whether *Rozvoz - nástenka* may offer an order on this channel to external couriers - §9.792, owner O144.
 *
 * *"So if they have two rozvoz channels - one can be like that, second one dont"*. **An unresolvable channel offers**,
 * the column's own default and {@link usesDrivers}' reasoning: a channel the board cannot read is not a decision to
 * keep its orders, and the server refuses an offer on a channel that does (`CHANNEL_NOT_OFFERED`) whatever this says.
 */
export function offersToCouriers(slug: string, channels: readonly FilterChannel[]): boolean {
  const channel = channels.find((entry) => entry.slug === slug)
  if (channel === undefined) return true
  return channel.offersToCouriers
}

/**
 * Whether somebody is standing at the counter waiting for this one.
 *
 * On the premises but not at a table. That is what `type === 'bar'` meant, and the reason the board sorts
 * these first: a person at the counter is absorbing the wait themselves, where a delivery still has a
 * driver's journey to absorb it.
 */
export function isCounterCollection(slug: string, channels: readonly FilterChannel[]): boolean {
  const channel = channels.find((entry) => entry.slug === slug)
  if (channel === undefined) return false
  return channel.onPremise && !channel.usesTables
}

/**
 * The registers the person printing chooses from on this channel, or none - `FRONTEND.md` §9.740, `BACKEND.md`
 * §10A.388, owner O110.
 *
 * **Only on a channel both on the premises and driven** - Sereď's *Osobný odber*, the one the owner asked about:
 * *"na prevádzke aj rozváža šofér je aktívne, tak tam pridáme Ďalšie"*. The channel editor offers the setting only
 * there and clears it when either capability is unticked, and this is the same condition from the reading side, so
 * a list somebody left behind cannot keep narrowing a till that draws no control to clear it.
 *
 * **Empty means no list**: the till keeps *Podľa kanála* and every register, the board keeps `rozvoz`. An unknown
 * channel has none.
 */
export function printRegistersOf(slug: string, channels: readonly FilterChannel[]): readonly string[] {
  const channel = channels.find((entry) => entry.slug === slug)
  if (channel === undefined) return []
  return channel.onPremise && channel.usesDrivers ? channel.printKasaIds : []
}

/**
 * Whether an order on this channel may be put on a tab.
 *
 * `OrderTabService.append` refuses otherwise, and says why: *"Letting any order onto a tab would take it
 * out of the day's takings — the handover excludes open tabs — so the channel's flag is what separates
 * **not yet paid** from **somebody forgot to charge for this**."*
 */
export function allowsTab(slug: string, channels: readonly FilterChannel[]): boolean {
  return channels.find((entry) => entry.slug === slug)?.allowsDeferredPayment === true
}

/**
 * What to call a sales channel on a report — `FRONTEND.md` §9.94.
 *
 * Four screens carried a hand-written slug-to-label map, which §9.5's rule is the opposite of: *ask
 * what a channel does, never what it is called*. A rename never reached them, a slug outside the map
 * printed raw, and one map named `pickup` where this franchise's slug is `bar` — so its *Osobný
 * odber* never resolved at all.
 *
 * The franchise's own `name` wins. `fallbacks` covers a slug the API did not return — a report drawn
 * before the channels have loaded still reads properly — and the slug itself is the last resort,
 * because a row labelled `na_sekeru` is worse than a good label and far better than a dropped row.
 */
export function channelLabel(
  slug: string | null | undefined,
  channels: readonly FilterChannel[],
  fallbacks: Readonly<Record<string, string>> = {},
): string {
  const wanted = (slug ?? '').trim()
  if (wanted === '') return ''
  const named = channels.find((channel) => channel.slug === wanted)?.name?.trim()
  if (named !== undefined && named !== '') return named
  return fallbacks[wanted] ?? wanted
}
